
import React, { useState } from 'react';
import { 
  Calendar, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  TrendingUp, 
  Pill, 
  Activity,
  Brain,
  Shield,
  Clock3,
  CalendarDays,
  Zap,
  Target,
  AlertCircle
} from 'lucide-react';

interface MedicationRecord {
  id: string;
  medicationName: string;
  dosage: string;
  scheduledTime: string;
  administeredTime?: string;
  status: 'scheduled' | 'administered' | 'missed' | 'overdue';
  nurse?: string;
  notes?: string;
}

interface AIInsight {
  type: 'adherence' | 'interaction' | 'timing' | 'dosage';
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  icon: React.ReactNode;
}

const EmarTab: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'today' | 'week' | 'month'>('today');

  // Mock data for medication records
  const medicationRecords: MedicationRecord[] = [
    {
      id: '1',
      medicationName: 'Augmentin 625mg',
      dosage: '1 tablet',
      scheduledTime: '08:00',
      administeredTime: '08:15',
      status: 'administered',
      nurse: 'Nurse Sarah',
      notes: 'Patient tolerated well'
    },
    {
      id: '2',
      medicationName: 'Panadol 500mg',
      dosage: '2 tablets',
      scheduledTime: '12:00',
      administeredTime: '12:05',
      status: 'administered',
      nurse: 'Nurse Sarah'
    },
    {
      id: '3',
      medicationName: 'Concor 5mg',
      dosage: '1 tablet',
      scheduledTime: '20:00',
      status: 'scheduled',
      nurse: 'Nurse Mike'
    },
    {
      id: '4',
      medicationName: 'Vitamin D3',
      dosage: '1 capsule',
      scheduledTime: '09:00',
      status: 'missed',
      nurse: 'Nurse Sarah'
    }
  ];

  // AI Insights
  const aiInsights: AIInsight[] = [
    {
      type: 'adherence',
      title: 'Excellent Adherence Rate',
      description: 'Patient has maintained 95% medication adherence over the past 30 days, which is above the recommended threshold.',
      severity: 'low',
      icon: <TrendingUp className="w-5 h-5 text-green-500" />
    },
    {
      type: 'timing',
      title: 'Optimal Dosing Schedule',
      description: 'Current medication timing is well-spaced and follows best practices for this combination of medications.',
      severity: 'low',
      icon: <Clock3 className="w-5 h-5 text-blue-500" />
    },
    {
      type: 'interaction',
      title: 'Potential Interaction Detected',
      description: 'Augmentin may interact with oral contraceptives. Consider backup contraception method.',
      severity: 'medium',
      icon: <AlertTriangle className="w-5 h-5 text-yellow-500" />
    },
    {
      type: 'dosage',
      title: 'Dosage Optimization',
      description: 'AI suggests considering morning administration for Vitamin D3 to improve absorption.',
      severity: 'low',
      icon: <Brain className="w-5 h-5 text-purple-500" />
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'administered':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'missed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'overdue':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      default:
        return <Clock className="w-5 h-5 text-blue-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'administered':
        return 'bg-green-50 border-green-200';
      case 'missed':
        return 'bg-red-50 border-red-200';
      case 'overdue':
        return 'bg-orange-50 border-orange-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'border-red-200 bg-red-50';
      case 'medium':
        return 'border-yellow-200 bg-yellow-50';
      default:
        return 'border-green-200 bg-green-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Electronic Medication Administration Record (eMAR)
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            AI-powered medication tracking and insights
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            {['today', 'week', 'month'].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode as any)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  viewMode === mode
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Administered Today
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">2</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Scheduled Today
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">4</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Missed Today
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">1</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <Activity className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Adherence Rate
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">95%</p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Insights Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <Zap className="w-5 h-5 text-purple-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              AI Insights & Recommendations
            </h3>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {aiInsights.map((insight, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${getSeverityColor(insight.severity)}`}
              >
                <div className="flex items-start space-x-3">
                  {insight.icon}
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                      {insight.title}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
                      {insight.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Medication Schedule */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Pill className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Today's Medication Schedule
              </h3>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <CalendarDays className="w-4 h-4" />
              <span>{new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="space-y-4">
            {medicationRecords.map((record) => (
              <div
                key={record.id}
                className={`p-4 rounded-lg border ${getStatusColor(record.status)}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {getStatusIcon(record.status)}
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {record.medicationName}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {record.dosage} • {record.scheduledTime}
                      </p>
                      {record.nurse && (
                        <p className="text-xs text-gray-500">
                          Assigned to: {record.nurse}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm font-medium capitalize">
                      {record.status}
                    </div>
                    {record.administeredTime && (
                      <div className="text-xs text-gray-500">
                        Given at {record.administeredTime}
                      </div>
                    )}
                  </div>
                </div>
                
                {record.notes && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>Notes:</strong> {record.notes}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmarTab; 